/*
 * � 2020 Microsoft. All rights reserved.
 */

define('Arkadium/GameEngine/Maths/Bezier2D',['Arkadium/GameEngine/Maths/Vector2'], function (Vector2) {
    "use strict";

    /// <summary>
    /// 2D Bezier path defined by a list of points.
    /// The supplied points are either control points or through points for the bezier.
    /// </summary>
    var Bezier2D = function (points, pointType) {
        this.initBezier2D(points, pointType);
    };

    /// <summary>
    /// Type of points used in Bezier path.
    /// </summary>
    Bezier2D.BezierPointType =
    {
        Control: 0,
        Through: 1
    };

    Bezier2D.prototype.initBezier2D = function (points, pointType) {
        this.Points = !points ? null : points.slice(0); //copy
        this.PointType = pointType || Bezier2D.BezierPointType.Control;
        this.Distance = 0;
        this.Curve = [];

        this.UpdatePath();
    };


    Bezier2D.prototype.PositionAt = function (t, pos) {
        pos = pos || new Vector2(0, 0);

        if (this.Curve == null || this.Curve.length == 0) {
            pos = new Vector2(0, 0);
            return;
        }

        var posDist = t * this.Distance;
        var distance = 0;
        var arcDist = 0;
        var segments = this.Curve.length - 3;
        var index;
        for (index = 0; index <= segments; index += 2) {
            arcDist = this.ArcLength(this.Curve[index], this.Curve[index + 1], this.Curve[index + 2]);
            if (distance + arcDist > posDist) {
                break;
            }
            distance += arcDist;
        }
        index = index > segments ? segments : index;

        var inc = posDist == this.Distance ? 1 : (posDist - distance) / arcDist;
        var sPt = this.Curve[index + 0];
        var cPt = this.Curve[index + 1];
        var ePt = this.Curve[index + 2];

        pos = new Vector2(this.Quadratic(inc, sPt.X, cPt.X, ePt.X), this.Quadratic(inc, sPt.Y, cPt.Y, ePt.Y));

        return pos;
    };


    Bezier2D.prototype.UpdatePath = function () {
        //http://code.google.com/p/tweensy/source/browse/trunk/releases/source/cs4/original/com/flashdynamix/motion/guides/Bezier2D.as

        if (this.Points.length <= 2) {
            return;
        }
        this.Curve = null;

        if (this.PointType == Bezier2D.BezierPointType.Through) {
            var iter = 0;
            var len = this.Points.length - 1;

            this.Curve = new Array(len * 2 + 1);

            var point = this.Points[0];
            this.Curve[iter++] = point;

            var controlPoint = this.Points[2].clone();
            controlPoint.X -= point.X;
            controlPoint.Y -= point.Y;
            controlPoint.X = controlPoint.X / 4;
            controlPoint.Y = controlPoint.Y / 4;
            controlPoint = this.Points[1] - controlPoint;

            point = this.Points[1];

            this.Curve[iter++] = controlPoint;
            this.Curve[iter++] = point;

            for (var i = 1; i < len; ++i) {
                controlPoint = this.Points[i] + (this.Points[i] - controlPoint);
                point = this.Points[i + 1];

                this.Curve[iter++] = controlPoint;
                this.Curve[iter++] = point;
            }
        }
        else {
            var len = 3 + ((this.Points.length - 3) / 2) * 2;
            this.Curve = new Array(len);

            for (var i = 0; i < len; ++i) {
                this.Curve[i] = this.Points[i];
            }
        }
        this.UpdateDistance();
    };

    Bezier2D.prototype.UpdateDistance = function () {
        this.Distance = 0;
        for (var i = 0; i <= this.Curve.length - 3; i += 2) {
            this.Distance += this.ArcLength(this.Curve[i], this.Curve[i + 1], this.Curve[i + 2]);
        }
    };

    //    #region Math Helpers

    Bezier2D.prototype.ArcLength = function (pt1, pt2, pt3) {
        var a1 = this.AngleBetween(pt1, pt2);
        var a2 = this.AngleBetween(pt2, pt3);
        if (a1 == a2 || (a2 == a1 + Math.PI)) {
            return this.DistanceBetween(pt1, pt3);
        }

        var vmDivisor = (pt2.Y - 0.5 * pt3.Y - 0.5 * pt1.Y);
        var vm = 0;
        if (vmDivisor != 0) {
            vm = -(pt2.X - 0.5 * pt3.X - 0.5 * pt1.X) / vmDivisor;
        }
        var vtDivisor = (vm * (pt1.X - 2 * pt2.X + pt3.X) - (pt1.Y - 2 * pt2.Y + pt3.Y));
        var vt = 0;
        if (vtDivisor != 0) {
            vt = (-vm * (pt2.X - pt1.X) + (pt2.Y - pt1.Y)) / vtDivisor;
        }

        var va = this.Angle(vt, pt1, pt2, pt3);

        var ra = this.ProjectDistance(pt1, pt2);
        var rb = this.ProjectDistance(pt2, pt3);
        var alfa = va - this.RadAngle(pt1, pt2);

        var beta = this.RadAngle(pt2, pt3) - va;
        var rax = ra * Math.cos(alfa);
        var ray = ra * Math.sin(alfa);

        var rbx = rb * Math.cos(beta);
        var rby = rb * Math.sin(beta);

        var l1 = 0.5 * this.ParabolaArcLength(ray, 2 * Math.abs(rax));
        var l2 = 0.5 * this.ParabolaArcLength(rby, 2 * Math.abs(rbx));

        return (rax < 0 || rbx < 0) ? Math.abs(l2 - l1) : l1 + l2;
    };

    Bezier2D.prototype.Quadratic = function (t, a, b, c) {
        return a + t * (2 * (1 - t) * (b - a) + t * (c - a));
    };

    Bezier2D.prototype.ParabolaArcLength = function (a, b) {
        if (a == 0) {
            return 0;
        }
        return  (.5 * Math.sqrt(b * b + 16 * a * a) + ((b * b) / (8 * a)) * Math.log((4 * a + Math.sqrt(b * b + 16 * a * a)) / b));
    };

    Bezier2D.prototype.RadAngle = function (pt1, pt2) {
        return Math.atan2(pt2.Y - pt1.Y, pt2.X - pt1.X);
    };

    Bezier2D.prototype.ProjectDistance = function (pt1, pt2) {
        return Math.sqrt(pt2.X * pt2.X + pt1.X * pt1.X - 2 * pt1.X * pt2.X + pt2.Y * pt2.Y + pt1.Y * pt1.Y - 2 * pt1.Y * pt2.Y);
    };

    Bezier2D.prototype.Angle = function (vt, pt1, pt2, pt3) {
        return (Math.atan2(this.Derivative(vt, pt1.Y, pt2.Y, pt3.Y), this.Derivative(vt, pt1.X, pt2.X, pt3.X)));
    };

    Bezier2D.prototype.Derivative = function (t, a, b, c) {
        return 2 * a * (t - 1) + 2 * b * (1 - 2 * t) + 2 * c * t;
    };

    Bezier2D.prototype.DistanceBetween = function (pt1, pt2) {
        var x = pt2.X - pt1.X;
        var y = pt2.Y - pt1.Y;
        return  Math.sqrt((x * x) + (y * y));
    };

    Bezier2D.prototype.AngleBetween = function (pt1, pt2) {
        var x = pt2.X - pt1.X;
        var y = pt2.Y - pt1.Y;
        return  Math.atan2(y, x);
    };

    //#endregion

    //#region List Impl

    Bezier2D.prototype.IndexOf = function (item) {
        return this.Points.indexOf(item);
    };

    Bezier2D.prototype.Insert = function (index, item) {
        this.Points.splice(index, 0, item);
        this.UpdatePath();
    };

    Bezier2D.prototype.RemoveAt = function (index) {
        this.Points.splice(index, 1);
        this.UpdatePath();
    };


    Bezier2D.prototype.Add = function (item) {
        this.Points.push(item);
        this.UpdatePath();
    };

    Bezier2D.prototype.Clear = function () {
        this.Points = [];
        this.UpdatePath();
    };

    Bezier2D.prototype.Contains = function (item) {
        return this.Points.indexOf(item) != -1;
    };
    /*
     Bezier2D.prototype.CopyTo(Vector2[] array, int arrayIndex)
     {
     Points.CopyTo(array, arrayIndex);
     }
     */

    Object.defineProperty(Bezier2D.prototype, "Count", {
        get: function () {
            return this.Points.length;
        }
    });


    Object.defineProperty(Bezier2D.prototype, "IsReadOnly", {
        get: function () {
            return false;
        }
    });


    Bezier2D.prototype.Remove = function (item) {
        var ret = null;
        var index = this.Points.indexOf(item);
        if (index != -1) {
            ret = this.Points.splice(index, 1);
        }
        this.UpdatePath();
        return ret;
    };


    return Bezier2D;

});
